import React from 'react';
import get from 'lodash/get';
import map from 'lodash/map';
import clone from 'lodash/clone';
import has from 'lodash/has';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import assign from 'lodash/assign';
import omitBy from 'lodash/omitBy';
import keyBy from 'lodash/keyBy';

import { CONNECTION_CATEGORY_TEMPLATIZED_API } from 'js/constants/connectionCategories';
import UIUtils from '../../../lib/UIUtils';
import EnterSourceCredentials from './credentials/EnterSourceCredentials';

import { SUPPORT_KEY_CREDENTIAL } from 'js/constants/supportPageKeys';

export const NEW_CREDS_VALUE = 'nex-new-creds';
const CREDS_WITHOUT_ID = 'nex-def-val';

const getSortedKey = (list, key) =>
  (list || [])
    .map((item) => item[key])
    .sort()
    .join(',');

class AuthenticateDataSource extends React.Component {
  constructor(props) {
    super(props);
    const effectiveType = props.effectiveCredentialType || props.selectedConnection;
    this.state = {
      credsToDisplay: this._getCredsToDisplay(props.dataCredentials, effectiveType),
    };

    this._getCredsToDisplay = this._getCredsToDisplay.bind(this);
    this._getCredFormData = this._getCredFormData.bind(this);

    this._handleCredSelected = this._handleCredSelected.bind(this);
    this._handleCredInfoUpdated = this._handleCredInfoUpdated.bind(this);

    this._onCloseNewCredentialsClicked = this._onCloseNewCredentialsClicked.bind(this);
  }

  componentDidMount() {
    const credFormData = this._getCredFormData();

    if (!credFormData.selectedCredentialInfo) {
      this.props.onFormInputChanged({
        selectedCredentialInfo: this.buildDefaultValuesSelectedCredentialInfo(),
        selectedCredentialId: get(credFormData, 'selectedCredentialId'),
      });
    }

    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_CREDENTIAL);
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_CREDENTIAL);
    }
  }

  componentDidUpdate(prevProps) {
    const effectiveType = this.props.effectiveCredentialType || this.props.selectedConnection;
    const prevEffectiveType = prevProps.effectiveCredentialType || prevProps.selectedConnection;
    const credsListChanged =
      this.props.credentialsById !== prevProps.credentialsById ||
      this.props.selectedConnection !== prevProps.selectedConnection ||
      effectiveType !== prevEffectiveType;
    if (credsListChanged) {
      const credsToDisplay = this._getCredsToDisplay(this.props.dataCredentials, effectiveType);
      // Include validStatus in the key so the list refreshes when probe results change
      // (e.g. a newly created credential transitions from no-status → invalid/valid).
      const prevKey = getSortedKey(
        this.state.credsToDisplay.map((c) => ({
          k: `${c.value}:${c.validStatus || ''}`,
        })),
        'k',
      );
      const nextKey = getSortedKey(
        credsToDisplay.map((c) => ({ k: `${c.value}:${c.validStatus || ''}` })),
        'k',
      );
      if (prevKey !== nextKey) {
        this.setState({ credsToDisplay });
        this.props.onFormInputChanged({ credsToDisplay }, false);
      }
    }

    // Only run when auth field list actually changed (by names), not on new array reference.
    // Parents like SqlRunnerSourceForm may pass a new reference each render; avoid update loop.
    const prevNames = getSortedKey(prevProps.connectionAuthFields, 'name');
    const currNames = getSortedKey(this.props.connectionAuthFields, 'name');
    if (prevNames !== currNames) {
      const initialCredentialInfo = this.buildDefaultValuesSelectedCredentialInfo();

      const prevAuthFieldsByName = keyBy(prevProps.connectionAuthFields, 'name');
      const newAuthFieldsByName = keyBy(this.props.connectionAuthFields, 'name');
      const credentialInfo = omitBy(
        this._getCredFormData().selectedCredentialInfo,
        (value, key) =>
          key !== '_vendorConnectionType' &&
          has(prevAuthFieldsByName, key) &&
          !has(newAuthFieldsByName, key),
      );

      this.props.onFormInputChanged({
        selectedCredentialInfo: {
          ...initialCredentialInfo,
          ...credentialInfo,
        },
      });
    }
  }

  buildDefaultValuesSelectedCredentialInfo(newCred = {}, isNewCred = true) {
    let selectedCredentialInfo = newCred || {};

    let connectionAuthFields = this.props.connectionAuthFields || [];
    connectionAuthFields.forEach((f) => {
      if (has(f, 'default') && !has(selectedCredentialInfo, f.name)) {
        selectedCredentialInfo[f.name] = f.default;
      }
    });

    return isNewCred
      ? UIUtils.configWithMissingDefaults(selectedCredentialInfo, connectionAuthFields)
      : selectedCredentialInfo;
  }

  render() {
    const credFormData = this._getCredFormData();
    const effectiveCredentialType =
      this.props.effectiveCredentialType || this.props.selectedConnection;

    return (
      <EnterSourceCredentials
        connectionTypes={this.props.connectionTypes}
        connectionMetas={this.props.connectionMetas}
        vendors={this.props.vendors}
        credsToDisplay={this.state.credsToDisplay}
        selectedCredentialId={credFormData.selectedCredentialId}
        selectedCredentialInfo={credFormData.selectedCredentialInfo}
        isNewCredential={credFormData.isNewCredential}
        isFormAwaitingValidation={this.props.isFormAwaitingValidation}
        isLoadingData={this.props.isLoadingData}
        selectedConnection={effectiveCredentialType}
        onCredSelected={this._handleCredSelected}
        onCredInfoUpdated={this._handleCredInfoUpdated}
        onCloseNewCredentialsClicked={this._onCloseNewCredentialsClicked}
        onRefreshClicked={this.props.onRefreshClicked}
        onSaveNewCredential={() => this.props.nextFormStage()} // invokes validationMethod={this._validateSelectCredential} in AddDataSourceForm
        onEditCredentialSuccess={() => this.props.nextFormStage()} // auto-advance after edit+probe succeeded (validator reads cached credentialProbeStatus)
        templates={this.props.templates}
        selectedTemplate={this.props.selectedTemplate}
        onChangeTemplate={this.props.onChangeTemplate}
        connectionAuthFields={this.props.connectionAuthFields}
        connectionAuthMeta={this.props.connectionAuthMeta}
        credentialInputDisabled={this.state.credentialInputDisabled}
        onChangeApiType={this.props.onChangeApiType}
      />
    );
  }

  _handleCredSelected(credId, authInfo, skipAutoRedirection) {
    let isNewCredential = credId === NEW_CREDS_VALUE;
    let credentialInfo = authInfo ? authInfo : null;

    if (isNewCredential === false && !authInfo) {
      /**
       * get the info for this cred from credsToDisplay
       */
      credentialInfo = find(this.state.credsToDisplay, function (cred) {
        return cred.value === credId;
      });
    }

    credentialInfo = this.buildDefaultValuesSelectedCredentialInfo(credentialInfo, isNewCredential);

    this.props.onFormInputChanged(
      {
        isNewCredential: isNewCredential,
        selectedCredentialId: credId,
        // clear out previous credential info
        selectedCredentialInfo: UIUtils.fillConditionalDefault(
          credentialInfo,
          this.props.connectionAuthFields,
        ),
      },
      !isNewCredential,
    );

    this.props.onCredSelectionChanged(credentialInfo, !isNewCredential, skipAutoRedirection);
    this.setState({ credentialInputDisabled: !!authInfo });
  }

  _handleCredInfoUpdated(credId, credInfo, field, value) {
    //TODO : remove any fields from state that were dependant on parent settings but are no longer valid
    this.props.onFormInputChanged({
      selectedCredentialInfo: UIUtils.fillConditionalDefault(
        credInfo,
        this.props.connectionAuthFields,
        field,
        value,
      ),
    });
  }

  _onCloseNewCredentialsClicked(event) {
    event?.preventDefault();

    this.props.onFormInputChanged({
      isNewCredential: false,
      selectedCredentialId: null,
      //Clear out previous credential info
      selectedCredentialInfo: this.buildDefaultValuesSelectedCredentialInfo(),
    });
  }

  _getCredsToDisplay(allDataCredentials, selectedConnectionType) {
    //1. filter out credentials for this destination type
    let credentialsToDisplay = [];
    if (allDataCredentials) {
      let credsForConn = allDataCredentials[selectedConnectionType];
      let sortedIds = get(credsForConn, 'sortedIds', []);
      let credsById = get(credsForConn, 'items', {});

      forEach(this.props.connectionMetas, (meta, connection) => {
        if (
          meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API &&
          meta.connectionType === selectedConnectionType
        ) {
          let vendorCreds = allDataCredentials[connection];
          let vendorSortedIds = get(vendorCreds, 'sortedIds', []);
          let vendorCredsById = get(vendorCreds, 'items', {});

          sortedIds = sortedIds.concat(vendorSortedIds);
          credsById = assign({}, credsById, vendorCredsById);
        }
      });

      credentialsToDisplay = map(sortedIds, (cId) => {
        //TODO: can we even have credentials without id??
        let cred = clone(credsById[cId]);
        cred.value = cred.id || CREDS_WITHOUT_ID;
        cred.label = cred.name || 'Untitled Credential';
        return cred;
      });
    }

    credentialsToDisplay.unshift({
      value: NEW_CREDS_VALUE,
      label: '+ Create New',
    });

    return credentialsToDisplay;
  }

  _getCredFormData() {
    return this.props.credFormData || this.props.formData;
  }
}

export default AuthenticateDataSource;
